import React, { useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import { generateIdempotencyKey, setPendingIdempotencyKey } from "../../utils/idempotencyKey";
import { formatCentavos } from "../../utils/whtMoney";
import { invalidateEmployees, employeeName } from "./WhtPickers";
import { CSV_TEMPLATE_HEADER } from "./compFields";

// CSV import of compensation rows (F7a): pick a file → dry-run preview (per
// line: employee match, totals, errors red, warnings amber) → optionally
// create missing employees → confirm (all-or-nothing — one bad line and
// nothing is saved). Every request is a POST, so each gets its own
// Idempotency-Key; a confirm retried with the same file + options reuses its
// key so a dropped response replays instead of importing twice.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(`${CSV_TEMPLATE_HEADER}\n`)}`;

const MATCH_LABEL = { tin: "Matched by TIN", name: "Matched by name", new: "New employee" };

const readFile = (file) => (typeof file.text === "function"
  ? file.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  }));

const messagesFor = (list, line) => list.filter((e) => e.line === line);
const fieldMessages = (e) => Object.entries(e.fields || {}).map(([f, m]) => `${f}: ${m}`);

const CompImportPanel = ({ client, onImported }) => {
  const clientId = client?._id;
  const fileInput = useRef(null);
  const confirmKeys = useRef(new Map()); // fingerprint → Idempotency-Key
  const [file, setFile] = useState(null); // { name, csv }
  const [createMissing, setCreateMissing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileError, setFileError] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setFileError(null);
    setCreateMissing(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const dryRun = async (csv, createMissingEmployees) => {
    setFileError(null);
    // A dry run saves nothing; a fresh key each time so a different file or
    // option never collides with an earlier request's key.
    setPendingIdempotencyKey(generateIdempotencyKey());
    try {
      const res = await withholdingTaxAPI.importCompCsv(clientId, csv, { dryRun: true, createMissingEmployees });
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      setFileError(e.message || "The file couldn't be read.");
    }
  };
  const { mutate: runDryRun, isPending: previewing } = useGuardedMutation(dryRun);

  const onPick = async (event) => {
    const picked = event.target.files?.[0];
    if (!picked) return;
    let csv;
    try {
      csv = await readFile(picked);
    } catch {
      setFileError("The file couldn't be read.");
      return;
    }
    setFile({ name: picked.name, csv });
    setCreateMissing(false);
    await runDryRun(csv, false);
  };

  const toggleCreateMissing = async (checked) => {
    setCreateMissing(checked);
    if (file) await runDryRun(file.csv, checked);
  };

  const confirm = async () => {
    const fingerprint = `${createMissing ? 1 : 0}:${file.csv}`;
    if (!confirmKeys.current.has(fingerprint)) confirmKeys.current.set(fingerprint, generateIdempotencyKey());
    setPendingIdempotencyKey(confirmKeys.current.get(fingerprint));
    try {
      const res = await withholdingTaxAPI.importCompCsv(clientId, file.csv, { dryRun: false, createMissingEmployees: createMissing });
      confirmKeys.current.delete(fingerprint);
      const saved = res.data?.saved?.length || 0;
      const created = res.data?.createdEmployees?.length || 0;
      notify.success("Imported", `${saved} compensation record${saved !== 1 ? "s" : ""}${created ? `, ${created} new employee${created !== 1 ? "s" : ""}` : ""}.`);
      if (created) invalidateEmployees(clientId);
      reset();
      onImported?.(res.data);
    } catch (e) {
      if (e.status) confirmKeys.current.delete(fingerprint); // rejected → a retry is a new request
      if (e.lineErrors?.length) setPreview((p) => (p ? { ...p, errors: e.lineErrors } : p));
      setFileError(e.status
        ? `${e.message || "The import was rejected"} — nothing was imported.`
        : "No response from the server — the import may or may not have been saved. Check the records below before importing again.");
      notify.error("Couldn't import", e.message || "Nothing was imported.");
    }
  };
  const { mutate: runConfirm, isPending: importing } = useGuardedMutation(confirm);

  const errors = preview?.errors || [];
  const warnings = preview?.warnings || [];
  const rows = preview?.rows || [];
  const totals = rows.reduce((a, r) => ({ total: a.total + (r.totalCompCentavos || 0), withheld: a.withheld + (r.taxWithheldCentavos || 0) }), { total: 0, withheld: 0 });

  return (
    <section aria-labelledby="comp-import-heading" className="border border-ink-line rounded-lg bg-white p-4 mb-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 id="comp-import-heading" className="text-base font-medium text-ink">Import from CSV</h3>
          <p className="text-xs text-ink-body">One line per employee per month. Month is YYYY-MM; amounts in pesos. All-or-nothing — one bad line and nothing is imported.</p>
        </div>
        <div className="flex items-center gap-3">
          <a href={TEMPLATE_HREF} download="compensation-template.csv" className="text-sm text-primary-700 hover:text-primary-500 inline-flex items-center gap-1">
            <Download className="w-4 h-4" /> Download template
          </a>
          <Button variant="outline" size="sm" icon={Upload} onClick={() => fileInput.current?.click()} loading={previewing}>Import CSV</Button>
          <input ref={fileInput} type="file" accept=".csv,text/csv" aria-label="CSV file" className="hidden" onChange={onPick} />
        </div>
      </div>

      {fileError && <p role="alert" className="text-sm text-danger-500 mt-3">{fileError}</p>}

      {preview && (
        <div className="mt-4" data-testid="import-preview">
          <p className="text-sm text-ink-body mb-2">
            <span className="font-medium text-ink">{file?.name}</span> · {preview.lineCount} line{preview.lineCount !== 1 ? "s" : ""}
            {errors.length > 0 && <span className="ml-2 text-danger-500">{errors.length} with errors</span>}
            {warnings.length > 0 && <span className="ml-2 text-warning-700">{warnings.length} warning{warnings.length !== 1 ? "s" : ""}</span>}
          </p>
          <div className="overflow-x-auto border border-ink-line rounded-lg max-h-96">
            <table className="min-w-full divide-y divide-ink-line text-sm">
              <thead className="bg-ink-canvas">
                <tr className="text-xs font-medium text-ink-body uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Line</th>
                  <th className="px-3 py-2 text-left">Month</th>
                  <th className="px-3 py-2 text-left">Employee</th>
                  <th className="px-3 py-2 text-right">Total comp (14)</th>
                  <th className="px-3 py-2 text-right">Tax withheld (25)</th>
                  <th className="px-3 py-2 text-left">Issues</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-line">
                {rows.map((r) => {
                  const lineErrors = messagesFor(errors, r.line).flatMap(fieldMessages);
                  const lineWarnings = messagesFor(warnings, r.line).map((w) => w.message);
                  return (
                    <tr key={r.line} data-line={r.line} data-status={lineErrors.length ? "error" : lineWarnings.length ? "warning" : "ok"}
                      className={lineErrors.length ? "bg-danger-50" : lineWarnings.length ? "bg-warning-50" : ""}>
                      <td className="px-3 py-2 tabular-nums text-ink-body">{r.line}</td>
                      <td className="px-3 py-2 text-ink">{r.month ? `${MONTHS[r.month - 1]} ${r.year}` : "—"}</td>
                      <td className="px-3 py-2 text-ink">
                        {r.employee ? employeeName(r.employee) : <span className="text-danger-500">Not found</span>}
                        {r.employee && (
                          <span className={`block text-xs ${r.employee.matchedBy === "new" ? "text-primary-700" : "text-ink-body"}`}>{MATCH_LABEL[r.employee.matchedBy]}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink">{r.totalCompCentavos != null ? `₱${formatCentavos(r.totalCompCentavos)}` : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink">{r.taxWithheldCentavos != null ? `₱${formatCentavos(r.taxWithheldCentavos)}` : "—"}</td>
                      <td className="px-3 py-2">
                        {lineErrors.map((m) => <p key={m} className="text-xs text-danger-500" data-testid="line-error">{m}</p>)}
                        {lineWarnings.map((m) => <p key={m} className="text-xs text-warning-700" data-testid="line-warning">{m}</p>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-ink-canvas border-t-2 border-ink-line">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-sm font-medium text-ink-body">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-ink" data-testid="import-total-14">₱{formatCentavos(totals.total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-ink" data-testid="import-total-25">₱{formatCentavos(totals.withheld)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={createMissing} disabled={previewing || importing}
                onChange={(e) => toggleCreateMissing(e.target.checked)} className="h-4 w-4 accent-primary-500" />
              Create missing employees
              {preview.employeesToCreate?.length > 0 && (
                <span className="text-xs text-ink-body">({preview.employeesToCreate.length} will be added, as non-MWE)</span>
              )}
            </label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset} disabled={importing}>Cancel</Button>
              <Button size="sm" onClick={() => runConfirm()} loading={importing} disabled={importing || previewing || errors.length > 0 || rows.length === 0}>
                Confirm import
              </Button>
            </div>
          </div>
          {errors.length > 0 && <p className="text-xs text-danger-500 mt-1 text-right">Fix the lines in red and pick the file again — nothing is imported while any line has an error.</p>}
        </div>
      )}
    </section>
  );
};

export default CompImportPanel;
